<template>
  <v-container fluid>
    <UniversalTable
      :config="tableConfig.tableParams.value"
      :items="filteredEmployees"
      @onActionClick="
        employees.setCurrentEmployee(null);
        viewState.state.addEmployeeModal.show = true;
      "
      @onSearch="viewState.handleSearch"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      @removeAllFilters="
        viewState.state.filters.values = [];
        viewState.removeAllFiltersAndReload();
      "
      @onSort="viewState.handleEmployeeSort"
      backend-pagination
      @onRequest="viewState.handleRequest"
      :externalCurrentPage="Math.floor(paginationDefaultParams.offset / 10) + 1"
      :items-count="employees.getEmployeesCount()"
      :cellContainerMaxWidth="
        tableConfig.tableParams.value.cellContainerMaxWidth
      "
    >
      <template #Dropdown-activator="{ item }">
        <EmployeeLineMenu
          :employee="{
            id: item?.id || 0,
            firstName: item?.first_name || '',
            lastName: item?.last_name || '',
            avatar: item?.avatar || '',
            email: item?.email || '',
            hasAccess: item?.access?.has_access || false,
            archived: item?.archived || false,
            isSuspended:
              item?.access?.is_suspended || item?.is_suspended || false,
          }"
          :status="item?.status || 'draft'"
          @action="
            (event) => {
              const actionMap = {
                rename: 'edit_action',
                changePicture: 'change_picture_action',
                restoreAccess: 'give_back_access_action',
                giveAccess: 'give_access_action',
                suspendAccess: 'suspend_access_action',
              };
              const actionValue =
                actionMap[event.type] || `${event.type}_action`;
              viewState.handleEmployeeDropdownOperation(
                { value: actionValue },
                item
              );
            }
          "
        />
      </template>
    </UniversalTable>
    <AddEmployeeDialog
      :show="viewState.state.addEmployeeModal.show"
      @close="viewState.state.addEmployeeModal.show = false"
      @onSave="
        viewState.handleEmployeeOperation(
          employees.operations.EDIT_EMPLOYEE,
          accountID,
          $event
        )
      "
      :propEmployee="
        employees.getCurrentEmployee()
          ? {
              id: employees.getCurrentEmployee()?.id || 0,
              first_name: employees.getCurrentEmployee()?.first_name || '',
              last_name: employees.getCurrentEmployee()?.last_name || '',
              title: employees.getCurrentEmployee()?.title || '',
              email: employees.getCurrentEmployee()?.email || '',
              availability: employees.getCurrentEmployee()?.availability || 0,
              avatar: employees.getCurrentEmployee()?.avatar || '',
              user: employees.getCurrentEmployee()?.user || '',
            }
          : null
      "
    ></AddEmployeeDialog>
    <ExEmployeePrimaryAssignmentWizard
      :label="'Position'"
      :items="viewState.state.primaryPositions.availablePositions"
      :currentEmployee="employees.getCurrentEmployee()"
      :value="viewState.state.assignmentWizard.show"
      :type="'scrollPositionPicker'"
      :isAssigning="viewState.state.assignmentWizard.isAssigning"
      @input="!$event ? viewState.closeAssignmentWizard() : ''"
      @onAssignPosition="
        viewState.onAssignPositionToEmployee({
          position: $event,
          accountId: accountID,
        })
      "
      @load-more="viewState.loadMorePrimaryPositions"
      @search="viewState.searchPrimaryPositions"
      :isLoadingMore="viewState.state.primaryPositions.isLoadingMore"
      :hasMoreData="viewState.state.primaryPositions.hasMoreData"
    />
    <ExEmployeeToPreparePosition
      :label="'Position'"
      :items="viewState.state.preparePositions.availablePositions"
      :currentEmployee="employees.getCurrentEmployee()"
      :value="viewState.state.preparePosition.show"
      :isAssigning="viewState.state.preparePosition.isAssigning"
      @input="!$event ? viewState.closePreparePosition() : ''"
      @onAssignPreparePosition="
        viewState.onAssignPreparePositionToEmployee({
          obj: $event,
          accountId: accountID,
        })
      "
      @load-more="viewState.loadMorePreparePositions"
      @search="viewState.searchPreparePositions"
      :isLoadingMore="viewState.state.preparePositions.isLoadingMore"
      :hasMoreData="viewState.state.preparePositions.hasMoreData"
    />
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />
    <RelievePrimaryAssignment
      v-if="viewState.state.relieveAssignment.show"
      :show="viewState.state.relieveAssignment.show"
      :type="viewState.state.relieveAssignment.type"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        user: employees.getCurrentEmployee()?.user || '',
        allData: employees.getCurrentEmployee() || '',
      }"
      :current-position="viewState.state.relieveAssignment.currentPosition.name"
      @onAction="
        viewState.relievePrimaryPositionFromEmployee(accountID, $event)
      "
      @onClose="viewState.closeRelieveAssignment()"
    />
    <TerminateEmployee
      @onAction="
        viewState.handleEmployeeOperation(
          employees.operations.TERMINATE_EMPLOYEE,
          accountID,
          $event
        )
      "
      @onClose="viewState.state.terminateEmployeeModal.show = false"
      :show="viewState.state.terminateEmployeeModal.show"
      :info="viewState.state.terminateEmployeeModal.message"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: `${employees.getCurrentEmployee()?.first_name || 'U'}`,
        lastName: `${employees.getCurrentEmployee()?.last_name || 'S'}`,
        allData: employees.getCurrentEmployee() || '',
      }"
    />
    <DeleteEmployee
      @onAction="
        viewState.handleEmployeeOperation(
          employees.operations.DELETE_EMPLOYEE,
          accountID
        )
      "
      @onClose="viewState.state.deleteEmployeeModal.show = false"
      :show="viewState.state.deleteEmployeeModal.show"
      :info="viewState.state.deleteEmployeeModal.message"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: `${employees.getCurrentEmployee()?.first_name || 'U'}`,
        lastName: `${employees.getCurrentEmployee()?.last_name || 'S'}`,
      }"
    />
    <ActivateEmployee
      :show="viewState.state.activeEmployeeModal.show"
      :employee="{
        picture: `${employees.getCurrentEmployee()?.avatar || ''}`,
        firstName: `${employees.getCurrentEmployee()?.first_name || 'U'}`,
        lastName: `${employees.getCurrentEmployee()?.last_name || 'S'}`,
      }"
      @onClose="viewState.state.activeEmployeeModal.show = false"
      @onActiveEmployee="
        viewState.handleEmployeeOperation(
          employees.operations.ACTIVATE_EMPLOYEE,
          accountID
        )
      "
    ></ActivateEmployee>
    <ReActivateEmployee
      :show="viewState.state.reactivateEmployeeModal.show"
      :employee="{
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        ...employees.getCurrentEmployee(),
      }"
      @onClose="viewState.state.reactivateEmployeeModal.show = false"
      @onActiveEmployee="
        viewState.handleEmployeeOperation(
          employees.operations.REACTIVATE_EMPLOYEE,
          accountID
        )
      "
    ></ReActivateEmployee>
    <EditModalPicture
      :dialog="viewState.state.changePictureModal.show"
      :avatar="employees.getCurrentEmployee()?.avatar || ''"
      @croppedImage="
        viewState.handleEmployeeOperation(
          employees.operations.CHANGE_EMPLOYEE_PICTURE,
          accountID,
          $event
        )
      "
      @closeModal="viewState.state.changePictureModal.show = false"
      @removeModal="
        viewState.handleEmployeeOperation(
          employees.operations.CHANGE_EMPLOYEE_PICTURE,
          accountID,
          null
        )
      "
    />
    <GiveAccessToEmployeeModal
      @checkEmail="
        viewState.checkUserEmail({ accountId: accountID, email: $event })
      "
      @onSave="
        viewState.giveAccessToEmployee({
          accountId: accountID,
          payload: $event,
        })
      "
      @onChangeContentType="viewState.state.giveAccessModal.dataType = $event"
      v-model="viewState.state.giveAccessModal.show"
      :contentTypeProp="viewState.state.giveAccessModal.dataType"
      :existingUser="viewState.state.giveAccessModal.existingUser"
      :access_level="viewState.state.giveAccessModal.access_level"
      :showEmailLoadingIcon="
        viewState.state.giveAccessModal.showEmailLoadingIcon
      "
      :current-employee="{
        id:
          (viewState.state.currentRow || employees.getCurrentEmployee())?.id ||
          0,
        firstName:
          (viewState.state.currentRow || employees.getCurrentEmployee())
            ?.first_name || '',
        lastName:
          (viewState.state.currentRow || employees.getCurrentEmployee())
            ?.last_name || '',
        availabilityVal: (
          viewState.state.currentRow || employees.getCurrentEmployee()
        )?.availability,
        avatar:
          (viewState.state.currentRow || employees.getCurrentEmployee())
            ?.avatar || '',
        email:
          (viewState.state.currentRow || employees.getCurrentEmployee())
            ?.email || '',
        primary_position: (
          viewState.state.currentRow || employees.getCurrentEmployee()
        )?.primary_position,
        preparing_for_positions: (
          viewState.state.currentRow || employees.getCurrentEmployee()
        )?.preparing_for_positions,
      }"
    />
    <GiveBackAccessToEmployeeModal
      @onSave="
        viewState.giveAccessToEmployee({
          accountId: accountID,
          payload: $event,
        })
      "
      @onChangeContentType="viewState.state.giveAccessModal.dataType = $event"
      v-model="viewState.state.giveBackAccessModal.show"
      :contentTypeProp="viewState.state.giveAccessModal.dataType"
      :existingUser="viewState.state.giveAccessModal.existingUser"
      :showEmailLoadingIcon="
        viewState.state.giveAccessModal.showEmailLoadingIcon
      "
      :current-employee="{
        id: employees.getCurrentEmployee()?.id || 0,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        availabilityVal: employees.getCurrentEmployee()?.availability,
        avatar: employees.getCurrentEmployee()?.avatar || '',
        email: employees.getCurrentEmployee()?.email || '',
      }"
    />
    <ArchivePopUp
      :action="viewState.state.archiveAction"
      type="employee"
      :show="viewState.state.archiveEmployeeModal.show"
      :curretObj="{
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.archiveEmployeeModal.show = false"
      @onArchive="
        viewState.handleEmployeeOperation(
          employees.operations.ARCHIVE_EMPLOYEE,
          accountID
        )
      "
    ></ArchivePopUp>
    <ExActionsPopUp
      v-if="viewState.state.suspendEmployeeAccessModal?.show"
      headline="Suspend Access"
      :htmlInfo="viewState.state.suspendEmployeeAccessModal.message"
      actionLabel="Suspend"
      :value="viewState.state.suspendEmployeeAccessModal?.show"
      :destructive="true"
      :avatar="{
        picture: viewState.state.currentEmployee?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onAction="OnSuspendUser"
      @input="OnCloseSuspendUserDialog"
    />

    <CannotRenameEmployeeModal
      :show="viewState.state.cannotRenamEmployeeModal.show"
      :avatar="{
        picture: viewState.state.currentEmployee?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.cannotRenamEmployeeModal.show = false"
      @onAction="viewState.state.cannotRenamEmployeeModal.show = false"
    />

    <ExActionsPopUp
      v-if="viewState.state.giveBackEmployeeAccessModal?.show"
      headline="Give back Access"
      :htmlInfo="viewState.state.giveBackEmployeeAccessModal.message"
      actionLabel="Give back Access"
      :value="viewState.state.giveBackEmployeeAccessModal?.show"
      :destructive="false"
      :avatar="{
        picture: viewState.state.currentEmployee?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onAction="OnGiveAccessBackUser"
      @input="OnGiveBackAccessDialog"
    />
    <ExPositionModal
      :dialog="viewState.state.assignmentPosition.show"
      :modalHeaderState="{
        status: viewState.state.assignmentPosition?.currentPosition?.status,
        icon: 'org_icon_position',
        code: getOrgCodeName(
          viewState.state.assignmentPosition?.currentPosition?.code_str
        ),
        name: viewState.state.assignmentPosition?.currentPosition?.name,
      }"
      @closeModal="OnClosePositionDialog"
      :tableData="viewState.state.assignmentItems"
      :items="menuItems"
      :pagination="viewState.state.paginationRoleModel"
      @onRequest="requestRoleData"
      @onSearchMode="requestSearch($event)"
      @onButtonAction="viewState.openRoleToPosition"
      @detachItem="handleDetachRole"
    />
    <AttachedRole
      v-model="viewState.state.showRoleToPosition"
      OrgObjectType="Role to Position"
      @onSave="viewState.attachedRole({ accountId: accountID })"
      :items="viewState.state.unmatchedResponsibilities"
      @item-selected="viewState.handleItemSelected"
      @add-item-selected="
        viewState.addHandleItemSelected({
          data1: $event,
          accountId: accountID,
        })
      "
      :parent-name="
        viewState.state.assignmentPosition?.currentPosition?.name || ''
      "
    />
    <ChecklistDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="viewState.state?.openChatterModal?.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      :typeValue="
        employees.getCurrentEmployee()?.first_name +
        ' ' +
        employees.getCurrentEmployee()?.last_name
      "
      @closeDrawer="viewState.closeEmployeeChatterModal()"
      :type="'Employee'"
      :chatterList="viewState.state?.openChatterModal?.chatterData"
      :totalCheckpointChatter="
        viewState.state?.openChatterModal?.totalEmployeeChatter
      "
      @requestMoreCheckpointsChatter="viewState.requestMoreChatter()"
    />
  </v-container>
</template>

<script setup>
import { onMounted, ref, getCurrentInstance, computed, watch } from "vue";
import { useEmployees } from "@/composables/employees/useEmployees";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import ExEmployeePrimaryAssignmentWizard from "@components/PositionPicker/ExEmployeePrimaryAssignmentWizard.vue";
import RelievePrimaryAssignment from "@components/organizations/employees/RelievePrimaryAssignment.vue";
import { useTableConfig } from "@/views/Employees/composables/useTableConfig";
import { useEmployeeViewState } from "@/views/Employees/composables/useEmployeeViewState";
import ReActivateEmployee from "@components/modals/ReActivateEmployee.vue";
import TerminateEmployee from "@components/organizations/employees/TerminateEmployee.vue";
import DeleteEmployee from "@components/organizations/employees/DeleteEmployee.vue";
import ActivateEmployee from "@components/modals/ActiveEmployee.vue";
import AddEmployeeDialog from "@components/organizations/employees/AddEmployeeDialog/AddEmployeeDialog.vue";
import EditModalPicture from "@components/EditPicture/EditModal.vue";
import GiveAccessToEmployeeModal from "@components/GiveAccessEmployee/GiveAccessToEmployeeModal.vue";
import GiveBackAccessToEmployeeModal from "@components/GiveBackAccessEmployee/GiveBackAccessToEmployeeModal.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import ExEmployeeToPreparePosition from "@components/PreparePositionPicker/ExEmployeeToPreparePosition.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import {
  userStateUpdateSuspend,
  userStateUpdateGiveBack,
  detachOrgObject,
} from "@/api";
import ExPositionModal from "@components/PositionModal/ExPositionModal.vue";
import AttachedRole from "@components/RolesCatalog/AttachedRole.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import CannotRenameEmployeeModal from "@components/organizations/employees/CannotRenameEmployeeModal.vue";
import EmployeeLineMenu from "@components/EmployeeLineMenu/EmployeeLineMenu.vue";

const app = getCurrentInstance();
const { proxy } = app;

const tableConfig = useTableConfig();
const viewState = useEmployeeViewState();
const employees = useEmployees();
const accountID = ref(JSON.parse(localStorage.getItem("currentAccountID")));
const paginationDefaultParams = employees.paginationDefaultParams;

// Create a reactive ref to track employees data
const employeesData = ref([]);

// Update employeesData whenever employees.allEmployees changes
watch(
  () => employees.allEmployees.value,
  (newVal) => {
    employeesData.value = newVal || [];
  },
  { immediate: true, deep: true }
);

watch(
  () => viewState.state.sanitizedItems,
  (newVal) => {
    if (newVal) {
      viewState.state.paginationRoleModel.total = newVal.filter(
        (el) => el.parentId === -1
      ).length;
    }
  },
  { immediate: true }
);

const menuItems = computed(() => [
  {
    title: "Roles attached",
    count: viewState.state.sanitizedItems.filter(
      (el) => el?.status === "active" && el?.parentId === -1
    ).length,
  },
  {
    title: "Assignments",
    count: 0,
  },
  { title: "Description", count: 0 },
  { title: "Chatter", count: 0 },
]);

// Computed property to track loader state

const filteredEmployees = computed(() => {
  // Use the reactive employeesData ref
  let allEmployees = employeesData.value || [];
  // Apply search filter
  // if (viewState.state.filters.searchText?.trim()) {
  //   const searchText =
  //     viewState?.state?.filters?.searchText?.toLowerCase() || "";
  //   allEmployees = allEmployees.filter((employee) => {
  //     const fullName =
  //       `${employee.first_name} ${employee.last_name}`.toLowerCase();
  //     return (
  //       fullName.includes(searchText) ||
  //       employee?.title?.toLowerCase().includes(searchText)
  //     );
  //   });
  // }

  // Filter by status
  // if (
  //   viewState.state.filters.values.some((val) =>
  //     viewState.state.filters.statusOptions.includes(val)
  //   )
  // ) {
  //   if (viewState.state.filters.values.includes("Show")) {
  //     allEmployees = allEmployees.filter((employee) =>
  //       viewState.state.filters.values.includes(employee.current_status)
  //     );
  //   } else {
  //     allEmployees = allEmployees.filter(
  //       (employee) =>
  //         viewState.state.filters.values.includes(employee.current_status) &&
  //         !employee.archived
  //     );
  //   }
  // }

  // Filter by availability
  // const availabilityFilters = ["Availability 0.5 FTE", "Availability 1 FTE"];
  // if (
  //   viewState.state.filters.values.some((val) =>
  //     availabilityFilters.includes(val)
  //   )
  // ) {
  //   allEmployees = allEmployees.filter((employee) => {
  //     return viewState.state.filters.values.some((filter) => {
  //       if (filter === "Availability 0.5 FTE") {
  //         return employee.availability === 0.5;
  //       } else if (filter === "Availability 1 FTE") {
  //         return employee.availability === 1;
  //       }
  //       return false;
  //     });
  //   });
  // }

  // Filter by archived status
  // const archivedFilters = ["Show", "Do not Show"];
  // if (
  //   viewState.state.filters.values.some((val) => archivedFilters.includes(val))
  // ) {
  //   allEmployees = allEmployees.filter((employee) => {
  //     return viewState.state.filters.values.some((filter) => {
  //       if (filter === "Show") {
  //         return true; // Include all archived and non-archived employees
  //       } else if (filter === "Do not Show") {
  //         return !employee.archived; // Exclude archived employees
  //       }
  //       return false;
  //     });
  //   });
  // }

  // Default filter when no filters are applied
  // if (viewState.state.filters.values.length === 0) {
  //   allEmployees = allEmployees.filter((employee) => !employee.archived);
  // }

  // // Sort employees by first name, then last name
  // return allEmployees.sort((a, b) => {
  //   const firstNameA = a?.first_name?.toLowerCase() || "";
  //   const firstNameB = b?.first_name?.toLowerCase() || "";
  //   const lastNameA = a?.last_name?.toLowerCase() || "";
  //   const lastNameB = b?.last_name?.toLowerCase() || "";

  //   if (firstNameA < firstNameB) return -1;
  //   if (firstNameA > firstNameB) return 1;

  //   if (lastNameA < lastNameB) return -1;
  //   if (lastNameA > lastNameB) return 1;

  //   return 0;
  // });
  return allEmployees;
});

const requestRoleData = (data) => {
  if (data && data.page) {
    viewState.state.paginationRoleModel.page = data.page;
    viewState.state.sanitizedItems = viewState.getSanitizedItems();
    viewState.state.assignmentItems = viewState.paginatedAttachedRoles(
      viewState.state.sanitizedItems,
      data.page
    );
  }
};

const requestSearch = (event) => {
  viewState.state.searchMode = event;
  viewState.state.sanitizedItems = viewState.getSanitizedItems();
  viewState.state.assignmentItems = viewState.paginatedAttachedRoles(
    viewState.state.sanitizedItems,
    viewState.state.paginationRoleModel.page
  );
};

const getOrgCodeName = (code) => {
  if (code) {
    return code?.split(" ")[0];
  }
};

const OnSuspendUser = async () => {
  proxy.$loader.setAppLoading(true);

  let obj = {
    email: employees.getCurrentEmployee()?.email,
    state: "suspended",
  };

  await userStateUpdateSuspend(
    viewState.state.currentRow?.account,
    viewState.state.currentRow?.access?.id,
    obj
  )
    .then(async () => {
      proxy.$loader.setAppLoading(false);
      // if (viewState.state.currentRow?.user) {
      //   viewState.state.currentRow.user.state = "suspended";
      // }
      proxy.$loader.show();
      employees.addEmployeeFilter("archived", false);
      await employees.loadAllEmployees();
      proxy.$loader.hide();
    })
    .catch((err) => {
      console.log(err);
      proxy.$loader.setAppLoading(false);
    })
    .finally(() => {
      viewState.state.suspendEmployeeAccessModal.show = false;
    });
};

const OnGiveAccessBackUser = async () => {
  proxy.$loader.setAppLoading(true);

  let obj = {
    email: employees.getCurrentEmployee()?.email,
    state: viewState.state.currentRow?.user?.state,
  };

  await userStateUpdateGiveBack(
    viewState.state.currentRow?.access?.account,
    viewState.state.currentRow?.access?.access_id,
    obj
  )
    .then(async () => {
      proxy.$loader.setAppLoading(false);
      proxy.$loader.show();
      await employees.loadAllEmployees();
      proxy.$loader.hide();
    })
    .catch((err) => {
      if (err.response?.data?.detail) {
        proxy.$toast.error(err.response.data.detail);
      } else {
        proxy.$toast.error("something went wrong, please try again.");
      }
      proxy.$loader.setAppLoading(false);
    })
    .finally(() => {
      viewState.state.giveBackEmployeeAccessModal.show = false;
    });
};

const OnCloseSuspendUserDialog = () => {
  viewState.state.suspendEmployeeAccessModal.show = false;
};

const OnGiveBackAccessDialog = () => {
  viewState.state.giveBackEmployeeAccessModal.show = false;
};

const OnClosePositionDialog = () => {
  viewState.state.assignmentPosition.show = false;
};

const handleDetachRole = async (detachData) => {
  try {
    proxy.$loader.setAppLoading(true);

    const { target, detachObj } = detachData;
    const payload = {
      childType: target.objectType.toLowerCase(), // "role"
      childId: target.objectId, // the role ID
      parentData: {
        parent_id: detachObj.id, // the position ID
      },
    };

    await detachOrgObject(accountID.value, payload);
    await viewState.reloadRoleData();

    viewState.state.sanitizedItems = viewState.getSanitizedItems();
    viewState.state.assignmentItems = viewState.paginatedAttachedRoles(
      viewState.state.sanitizedItems,
      viewState.state.paginationRoleModel.page
    );

    this.$notifications.success("Role detached successfully from position.");

    if (detachData.callBackFunc) {
      detachData.callBackFunc();
    }
  } catch (error) {
    console.error("Error detaching role from position:", error);
    if (error.response?.data?.detail) {
      proxy.$toast.error(error.response.data.detail);
    } else {
      proxy.$toast.error(
        "Failed to detach role from position. Please try again."
      );
    }
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

onMounted(async () => {
  await proxy.$watchFlag("show-assignment-creation-progress");
  viewState.state.showAssignmentCreationFlag =
    proxy.flags["show-assignment-creation-progress"];
  viewState.state.assignmentPosition.show = false;
  await viewState.clearSearch();
  if (
    employees.getDynamicEmployeeFilters()?.first_name ||
    employees.getDynamicEmployeeFilters()?.last_name
  ) {
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
  }
  employees.setPaginationDataOffset(0);
  // Clear filters if coming from login to prevent filter persistence after logout/login
  if (
    sessionStorage.getItem("navigated") !== null &&
    sessionStorage.getItem("navigated") == "Login"
  ) {
    viewState.state.filters.values = [];
    // Clear from localStorage as well to ensure complete cleanup
    viewState.removeAllFiltersAndReload();
    return;
  }

  // Load saved filters from localStorage before checking if we need to apply them
  viewState.loadFiltersOnReload();

  // await employees.loadAllEmployeesByBatches();
  employees.addEmployeeFilter("archived", false);
  if (viewState.state.filters.values.length > 0) {
    await viewState.applyFilters(viewState.state.filters.values);
  } else {
    await employees.loadAllEmployees();
  }
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
}
::v-deep .custom-assign .allocation_icon > .content {
  top: 45%;
}
::v-deep .ex-universal-table-container {
  overflow-x: hidden !important;
}
::v-deep .ex-universal-table .v-data-table__wrapper {
  overflow-x: hidden !important;
}
</style>
